import * as React from 'react';
import type {BookingInfoProps} from './booking-info.type';

import {useSelector} from '@xstate/react';
import {useRootMachine} from '../../hooks/useRootMachine';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {SequentialBookingInfo} from './sequential-booking-info/sequential-booking-info';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {selectEventTemplate} from '../../selectors/booking-selector';
import {selectRescheduleLimit} from '../../selectors/commonSelector';
import {RootMachineContext} from '../../machines/rootMachine/rootMachine.context';

import {
  BodySmall,
  BodySmallBold,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {useIsMobile} from 'src/components/scheduler/booking-v2/hooks/useIsMobile';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon/SemanticIcon';
import {CustomAccordion} from '../accordion/custom-accordion';

import css from './booking-info.css';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';
import {TEST_ID} from '../../utils/testId';


export const BookingInfo = ({
  title,
  brandLogo,
  bookingInfoItems,
}: BookingInfoProps) => {
  const isMobileView = useIsMobile();
  const {t} = useLabelTranslation();
  const {rootState} = useRootMachine();
  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);
  const isReschedule = rootState.matches('rescheduleFlow');
  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;

  const {bookingActor} = useBookingMachine();
  const eventTemplate = useSelector(bookingActor, selectEventTemplate);
  const isSequentialTemplate =
    eventTemplate?.distribution_rule === 'sequential_interview';

  if (isSequentialTemplate) {
    return (
      <SequentialBookingInfo
        title={title}
        brandLogo={brandLogo}
        eventTemplate={eventTemplate}
      />
    );
  }

  const header = (
    <div className={css.bookingInfoHeader}>
      {brandLogo && (
        <img
          src={brandLogo}
          // eslint-disable-next-line @spaced-out/i18n/no-static-labels
          alt="agency logo"
          className={css.logoPreview}
        />
      )}
      {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
      <SubTitleMedium color="primary">{title || ''}</SubTitleMedium>
    </div>
  );

  const content = (
    <div className={css.bookingInfoContent}>
      <div className={css.bookingInfoItems}>
        {bookingInfoItems.map((item, index: number) => (
          <div key={item.label + index} className={css.bookingInfoItem}>
            <SemanticIcon
              size="small"
              type="solid"
              semantic="neutral"
              name={item.icon}
            />
            <div className={css.bookingInfoItemText}>
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              <BodySmallBold>{item.label}</BodySmallBold>
              {item.subLabel ? (
                // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
                <BodySmall color="secondary">{item.subLabel}</BodySmall>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      {showRescheduleLimit && (
        <InContextAlert semantic="information">
          {`${t('You_have_rescheduleLimit_reschedules_left', {
            rescheduleLimit,
          })}.`}
        </InContextAlert>
      )}
    </div>
  );

  return (
    <div className={css.bookingInfoContainer}>
      {isMobileView ? (
        <CustomAccordion
          header={header}
          content={content}
          testId={generateTestId({
            base: TEST_ID.BOOKING_FLOW,
            slot: 'booking-info-accordion',
          })}
        />
      ) : (
        <>
          {header}
          {content}
        </>
      )}
    </div>
  );
};
